/**
 * Console Capture Utility for JIRA Help Desk System
 * Captures browser console logs for support ticket submission
 * Includes filtering for sensitive data and PHI protection
 */

class ConsoleCapture {
  constructor() {
    this.logs = [];
    this.maxLogs = 100;
    this.isCapturing = false;
    this.originalConsole = {
      log: console.log,
      warn: console.warn,
      error: console.error,
      info: console.info,
      debug: console.debug
    };
    
    this.init();
  }

  /**
   * Initialize console override and start capturing
   */
  init() {
    console.log('[Console Capture] Initializing console log capture system');
    this.startCapture();
    this.isCapturing = true;
  }

  /**
   * Start capturing console logs by overriding console methods
   */
  startCapture() {
    const self = this;

    // Override console.log
    console.log = function(...args) {
      self.originalConsole.log.apply(console, args);
      self.captureLog('log', args);
    };

    // Override console.warn  
    console.warn = function(...args) {
      self.originalConsole.warn.apply(console, args);
      self.captureLog('warn', args);
    };

    // Override console.error
    console.error = function(...args) {
      self.originalConsole.error.apply(console, args);
      self.captureLog('error', args);
    };

    // Override console.info
    console.info = function(...args) {
      self.originalConsole.info.apply(console, args);
      self.captureLog('info', args);
    };

    // Override console.debug
    console.debug = function(...args) {
      self.originalConsole.debug.apply(console, args);
      self.captureLog('debug', args);
    };
  }

  /**
   * Capture individual log entry
   */
  captureLog(level, args) {
    try {
      const timestamp = new Date().toISOString();
      const message = args.map(arg => {
        if (typeof arg === 'object') {
          return JSON.stringify(arg, null, 2);
        }
        return String(arg);
      }).join(' ');

      const logEntry = {
        timestamp,
        level,
        message: this.filterSensitiveData(message),
        url: window.location.href
      };

      // Add to logs array
      this.logs.push(logEntry);

      // Keep only the last maxLogs entries
      if (this.logs.length > this.maxLogs) {
        this.logs.shift();
      }
    } catch (error) {
      // Use original console.error to avoid recursion
      this.originalConsole.error('[Console Capture] Error capturing log:', error);
    }
  }

  /**
   * Filter sensitive data from log messages to protect PHI and security
   */
  filterSensitiveData(message) {
    if (!message || typeof message !== 'string') {
      return message;
    }

    const sensitivePatterns = [
      // JWT tokens
      {
        pattern: /Bearer\s+[A-Za-z0-9\-_=]+\.[A-Za-z0-9\-_=]+\.[A-Za-z0-9\-_.+/=]*/g,
        replacement: '[JWT_TOKEN_FILTERED]'
      },
      // API keys
      {
        pattern: /[Aa][Pp][Ii][_-]?[Kk][Ee][Yy][\s:=]+[A-Za-z0-9\-_]{20,}/g,
        replacement: '[API_KEY_FILTERED]'
      },
      // Email addresses (potential PHI)
      {
        pattern: /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g,
        replacement: '[EMAIL_FILTERED]'
      },
      // Phone numbers (potential PHI)
      {
        pattern: /(?:\+?1[-.\s]?)?\(?[0-9]{3}\)?[-.\s]?[0-9]{3}[-.\s]?[0-9]{4}/g,
        replacement: '[PHONE_FILTERED]'
      },
      // Social Security Numbers
      {
        pattern: /\b\d{3}-\d{2}-\d{4}\b/g,
        replacement: '[SSN_FILTERED]'
      },
      // Medical Record Numbers
      {
        pattern: /\b[Mm][Rr][Nn][\s:]+\d+/g,
        replacement: '[MRN_FILTERED]'
      },
      // Patient IDs
      {
        pattern: /\b[Pp]atient[\s_][Ii][Dd][\s:]+[A-Za-z0-9]+/g,
        replacement: '[PATIENT_ID_FILTERED]'
      },
      // MongoDB ObjectIds (potential patient identifiers)
      {
        pattern: /\b[0-9a-fA-F]{24}\b/g,
        replacement: '[OBJECT_ID_FILTERED]'
      },
      // Credit card numbers
      {
        pattern: /\b(?:\d{4}[-\s]?){3}\d{4}\b/g,
        replacement: '[CREDIT_CARD_FILTERED]'
      },
      // Passwords in URLs or forms
      {
        pattern: /password[\s:=]+[^\s&]+/gi,
        replacement: 'password=[FILTERED]'
      }
    ];

    let filteredMessage = message;
    sensitivePatterns.forEach(({ pattern, replacement }) => {
      filteredMessage = filteredMessage.replace(pattern, replacement);
    });

    return filteredMessage;
  }

  /**
   * Get filtered console logs for support ticket submission
   */
  getLogs(maxEntries = 50) {
    console.log(`[Console Capture] Retrieving last ${maxEntries} log entries`);
    
    const recentLogs = this.logs.slice(-maxEntries);
    
    // Further filter logs to exclude verbose framework logs
    const filteredLogs = recentLogs.filter(log => {
      const message = log.message.toLowerCase();
      
      // Filter out common framework noise
      const noisyPatterns = [
        'downloading chunk',
        'chunk loaded',
        'webpack-internal',
        'hot update',
        'hmr',
        '__webpack',
        'devtools',
        'react-hot-loader'
      ];
      
      return !noisyPatterns.some(pattern => message.includes(pattern));
    });

    // Prioritize errors and warnings
    const prioritizedLogs = [
      ...filteredLogs.filter(log => log.level === 'error'),
      ...filteredLogs.filter(log => log.level === 'warn'),
      ...filteredLogs.filter(log => ['log', 'info', 'debug'].includes(log.level))
    ];

    return prioritizedLogs.slice(-maxEntries);
  }

  /**
   * Format logs for JIRA ticket submission with size limiting
   */
  getFormattedLogs(maxEntries = 20, maxSizeKB = 50) {
    const logs = this.getLogs(maxEntries);
    
    if (logs.length === 0) {
      return 'No console logs captured';
    }

    let formatted = `Console logs (last ${logs.length} entries):\n\n`;
    let totalSize = formatted.length;
    const maxSizeBytes = maxSizeKB * 1024;
    
    for (const log of logs) {
      const timestamp = new Date(log.timestamp).toLocaleString();
      let logLine = `[${timestamp}] ${log.level.toUpperCase()}: ${log.message}\n`;
      
      // Truncate individual log entries that are too long
      if (logLine.length > 500) {
        logLine = logLine.substring(0, 497) + '...\n';
      }
      
      // Check if adding this log would exceed size limit
      if (totalSize + logLine.length > maxSizeBytes) {
        formatted += `\n[LOG SIZE LIMIT REACHED - Additional logs truncated]\n`;
        break;
      }
      
      formatted += logLine;
      totalSize += logLine.length;
    }

    console.log(`[Console Capture] Formatted logs size: ${Math.round(totalSize / 1024)}KB`);
    return formatted;
  }

  /**
   * Clear captured logs
   */
  clearLogs() {
    console.log('[Console Capture] Clearing captured logs');
    this.logs = [];
  }

  /**
   * Get capture statistics
   */
  getStats() {
    const logCounts = this.logs.reduce((counts, log) => {
      counts[log.level] = (counts[log.level] || 0) + 1;
      return counts;
    }, {});

    return {
      totalLogs: this.logs.length,
      isCapturing: this.isCapturing,
      logCounts,
      oldestLog: this.logs.length > 0 ? this.logs[0].timestamp : null,
      newestLog: this.logs.length > 0 ? this.logs[this.logs.length - 1].timestamp : null
    };
  }

  /**
   * Restore original console methods (for cleanup)
   */
  stopCapture() {
    console.log('[Console Capture] Stopping console log capture');
    
    console.log = this.originalConsole.log;
    console.warn = this.originalConsole.warn;
    console.error = this.originalConsole.error;
    console.info = this.originalConsole.info;
    console.debug = this.originalConsole.debug;
    
    this.isCapturing = false;
  }
}

// Create singleton instance
const consoleCapture = new ConsoleCapture();

// Export both the instance and the class
export { ConsoleCapture };
export default consoleCapture;